package org.example.project.components

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import org.example.project.PrimaryColor
import org.example.project.OrgTierDocument
import org.example.project.SystemProfileDocument
import org.example.project.UserDocument
import org.example.project.KYCType

@Composable
fun LevelInfoSection(currentSystemProfile: SystemProfileDocument?, orgLevels: List<OrgTierDocument>) {
    Card(
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(modifier = Modifier.padding(24.dp).fillMaxWidth()) {
            Text(
                text = "Enterprise Licensing Tiers",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = Color(0xFF64748B) // Slate 500
            )
            Spacer(modifier = Modifier.height(16.dp))

            val orgMeta = currentSystemProfile?.org_metadata
            if (orgMeta != null) {
                // Main Level
                Column {
                    Text(
                        text = "Current Tier",
                        style = MaterialTheme.typography.labelMedium,
                        color = Color(0xFF64748B)
                    )
                    Text(
                        text = "Tier ${orgMeta.level}",
                        style = MaterialTheme.typography.displaySmall,
                        fontWeight = FontWeight.Bold,
                        color = PrimaryColor
                    )
                    
                    val kycDisplay = when (orgMeta.kyc_type) {
                        KYCType.NONE -> "Unverified (Basic Access)"
                        KYCType.CORPORATE -> "Corporate Verified"
                    }
                    Text(
                        text = kycDisplay,
                        style = MaterialTheme.typography.bodySmall,
                        color = Color(0xFF94A3B8), // Slate 400
                        modifier = Modifier.padding(top = 4.dp)
                    )
                }

                Spacer(modifier = Modifier.height(20.dp))
                HorizontalDivider(color = Color(0xFFF1F5F9)) // Slate 100
                Spacer(modifier = Modifier.height(20.dp))

                // Limits
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text("Daily Quota", style = MaterialTheme.typography.labelMedium, color = Color(0xFF64748B))
                        Text("$${orgMeta.provision_daily_limit_usd}", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold, color = Color(0xFF334155))
                        val dailyRemaining = (orgMeta.provision_daily_limit_usd - orgMeta.provision_daily_volume_usd).coerceAtLeast(0)
                        Text("Available: $$dailyRemaining", style = MaterialTheme.typography.bodySmall, color = Color(0xFF16A34A), fontWeight = FontWeight.Medium, modifier = Modifier.padding(top = 4.dp))
                    }
                    Column(modifier = Modifier.weight(1f)) {
                        Text("Monthly Quota", style = MaterialTheme.typography.labelMedium, color = Color(0xFF64748B))
                        Text("$${orgMeta.provision_monthly_limit_usd}", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold, color = Color(0xFF334155))
                        val monthlyRemaining = (orgMeta.provision_monthly_limit_usd - orgMeta.provision_monthly_volume_usd).coerceAtLeast(0)
                        Text("Available: $$monthlyRemaining", style = MaterialTheme.typography.bodySmall, color = Color(0xFF16A34A), fontWeight = FontWeight.Medium, modifier = Modifier.padding(top = 4.dp))
                    }
                }

                Spacer(modifier = Modifier.height(24.dp))

                // Next Level Progress
                val nextLevel = orgLevels.find { it.level_id == orgMeta.level + 1 }
                val isMaxLevel = orgLevels.find { it.level_id == orgMeta.level }?.is_max_level == true

                if (isMaxLevel) {
                    Surface(
                        color = Color(0xFFF0FDF4), // Green 50
                        shape = MaterialTheme.shapes.small,
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(
                            text = "Top tier reached.",
                            color = Color(0xFF16A34A), // Green 600
                            modifier = Modifier.padding(12.dp),
                            style = MaterialTheme.typography.bodySmall,
                            fontWeight = FontWeight.Medium
                        )
                    }
                } else if (nextLevel != null) {
                    val currentVolume = orgMeta.provision_total_volume_usd.toFloat()
                    val targetThreshold = nextLevel.provision_total_threshold_usd.toFloat()
                    // Clamp progress between 0 and 1
                    val progress = if (targetThreshold > 0) (currentVolume / targetThreshold).coerceIn(0f, 1f) else 1f
                    
                    Column(modifier = Modifier.fillMaxWidth()) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Text(
                                text = "Next Tier: Tier ${nextLevel.level_id}",
                                style = MaterialTheme.typography.labelMedium,
                                color = Color(0xFF334155),
                                fontWeight = FontWeight.SemiBold
                            )
                            
                            val kycReqDisplay = when (nextLevel.required_kyc_type) {
                                KYCType.NONE -> "No Verification Req."
                                KYCType.CORPORATE -> "Corporate Verification Req."
                            }
                            Text(
                                text = kycReqDisplay,
                                style = MaterialTheme.typography.labelSmall,
                                color = Color(0xFF64748B)
                            )
                        }
                        
                        Spacer(modifier = Modifier.height(8.dp))
                        
                        LinearProgressIndicator(
                            progress = { progress },
                            modifier = Modifier.fillMaxWidth().height(8.dp).clip(RoundedCornerShape(4.dp)),
                            color = PrimaryColor,
                            trackColor = Color(0xFFE2E8F0) // Slate 200
                        )
                        
                        Spacer(modifier = Modifier.height(8.dp))
                        
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Text(
                                text = "Current: $${orgMeta.provision_total_volume_usd}",
                                style = MaterialTheme.typography.bodySmall,
                                color = Color(0xFF64748B)
                            )
                            Text(
                                text = "Goal: $${nextLevel.provision_total_threshold_usd}",
                                style = MaterialTheme.typography.bodySmall,
                                color = Color(0xFF64748B),
                                fontWeight = FontWeight.Medium
                            )
                        }
                    }
                }
            } else {
                Text("No entity metadata available.", color = Color(0xFF94A3B8), style = MaterialTheme.typography.bodyMedium)
            }
        }
    }
}
